import { create } from 'zustand';

/**
 * Auth state for the admin SPA.
 *
 * Phase 3 ships real email+password login via `/v1/auth/{register,
 * login}`. The admin stores the returned Sanctum bearer token in
 * localStorage (same trust boundary as before) but the user never
 * sees or types the raw token. A signed-in user has both a `user`
 * profile (email, name) and a list of `spaces` they own — the editor
 * opens against `spaceSlug`, defaulting to the first space when more
 * than one exists.
 *
 * **Token-paste path stays available** for developers who issue
 * tokens via `php artisan tinker`. The LoginForm has a small "Have
 * a token?" link that pivots into the TokenPasteShell.
 *
 * Field naming: `spaceSlug` (single, currently-active space) is kept
 * as the canonical name across the admin — everything from
 * EditorShell to the AI buttons reads it. The new `spaces` array is
 * additive.
 */

const STORAGE_TOKEN = 'peacock_admin_token';
const STORAGE_SPACE = 'peacock_admin_current_space';

export interface UserProfile {
  id: number;
  name: string;
  email: string;
  tenant_id: number | null;
}

export interface SpaceSummary {
  slug: string;
  name: string;
}

export interface AuthState {
  token: string | null;
  user: UserProfile | null;
  spaces: SpaceSummary[];
  /** Currently-active Space slug — what the editor edits. */
  spaceSlug: string | null;
  abilities: string[];
  activeLocale: string;

  // ── New API (email+password flow) ─────────────────────────────────
  /** Persist a token alongside known user + spaces (from /auth/login). */
  setSession(token: string, user?: UserProfile, spaces?: SpaceSummary[]): void;
  /** Update identity after /v1/auth/me returns. */
  setUserAndSpaces(user: UserProfile, spaces: SpaceSummary[]): void;
  /** Switch active Space (after the user picks one). */
  setCurrentSpace(slug: string | null): void;

  // ── Legacy API (kept for backwards-compat with existing consumers) ─
  /** Set token only (token-paste mode, no user profile yet). */
  setToken(token: string): void;
  /** Old-style identity setter — kept as alias for code that hasn't migrated. */
  setIdentity(spaceSlug: string | null, abilities: string[]): void;

  setActiveLocale(code: string): void;
  /** Clear everything — logout. */
  signOut(): void;
}

function readPersisted(key: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writePersisted(key: string, value: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    // Private-mode storage refusal — fall back to in-memory.
  }
}

export const useAuthStore = create<AuthState>((set) => ({
  token: readPersisted(STORAGE_TOKEN),
  user: null,
  spaces: [],
  spaceSlug: readPersisted(STORAGE_SPACE),
  abilities: [],
  activeLocale: 'de-AT',

  setSession(token, user, spaces) {
    writePersisted(STORAGE_TOKEN, token);
    set((s) => {
      const nextSpaces = spaces ?? s.spaces;
      // Pick a default space if we just got the list and the user
      // has no saved active slug yet.
      const validCurrent =
        s.spaceSlug !== null && nextSpaces.some((sp) => sp.slug === s.spaceSlug);
      const nextSlug = validCurrent
        ? s.spaceSlug
        : nextSpaces.length > 0
          ? nextSpaces[0]!.slug
          : null;
      if (nextSlug !== s.spaceSlug) writePersisted(STORAGE_SPACE, nextSlug);
      return {
        token,
        user: user ?? s.user,
        spaces: nextSpaces,
        spaceSlug: nextSlug,
      };
    });
  },

  setUserAndSpaces(user, spaces) {
    set((s) => {
      const validCurrent =
        s.spaceSlug !== null && spaces.some((sp) => sp.slug === s.spaceSlug);
      const nextSlug = validCurrent
        ? s.spaceSlug
        : spaces.length > 0
          ? spaces[0]!.slug
          : null;
      if (nextSlug !== s.spaceSlug) writePersisted(STORAGE_SPACE, nextSlug);
      return { user, spaces, spaceSlug: nextSlug };
    });
  },

  setCurrentSpace(slug) {
    writePersisted(STORAGE_SPACE, slug);
    set({ spaceSlug: slug });
  },

  // ── Legacy API ────────────────────────────────────────────────────
  setToken(token) {
    writePersisted(STORAGE_TOKEN, token);
    set({ token });
  },

  setIdentity(spaceSlug, abilities) {
    writePersisted(STORAGE_SPACE, spaceSlug);
    set({ spaceSlug, abilities });
  },

  setActiveLocale(code) {
    set({ activeLocale: code });
  },

  signOut() {
    writePersisted(STORAGE_TOKEN, null);
    writePersisted(STORAGE_SPACE, null);
    set({
      token: null,
      user: null,
      spaces: [],
      spaceSlug: null,
      abilities: [],
      activeLocale: 'de-AT',
    });
  },
}));
